import ExcelJS from 'exceljs';
import { format } from 'date-fns';
import { enUS, ptBR } from 'date-fns/locale';
import { parseLocalDate } from '@/utils/date';
import { capitalize, stripHtml } from '@/utils/formatters';
import { NO_PROJECT_COLOR } from '@/utils/projectColor';
import type { ColumnKey } from '@/features/reports/components/ColumnSelector';

export interface ReportTask {
  id: string;
  date: string;
  title: string;
  description: string | null | undefined;
  durationMinutes: number;
  hourlyRate: number;
  currency: string;
  totalAmount: number;
  billingStatus: string;
  project: { id: string; name: string; color?: string | null } | null | undefined;
  // 1 or 2 members — see issue #8. The Excel Member column joins the names
  // with ", " so a pair-work row reads as "Alice, Bob". Readonly because
  // Relay hands us a readonly array; callers should `.slice()` if they need
  // to mutate.
  members: ReadonlyArray<{ id: string; name: string }>;
}

export interface ReportFilters {
  startDate: string | null;
  endDate: string | null;
  projectId: string | null;
  billingStatus: string | null;
  memberIds: string[] | null;
}

export type ExportLocale = 'en-US' | 'pt-BR';

const COLUMN_LABELS: Record<ColumnKey, { en: string; pt: string }> = {
  date: { en: 'Date', pt: 'Data' },
  member: { en: 'Member', pt: 'Membro' },
  title: { en: 'Title', pt: 'Título' },
  description: { en: 'Description', pt: 'Descrição' },
  project: { en: 'Project', pt: 'Projeto' },
  duration: { en: 'Duration', pt: 'Duração' },
  hourlyRate: { en: 'Hourly Rate', pt: 'Valor Hora' },
  totalAmount: { en: 'Value', pt: 'Valor' },
  billingStatus: { en: 'Billing Status', pt: 'Status Faturamento' },
};

const COLUMN_WIDTHS: Record<ColumnKey, number> = {
  date: 12,
  member: 20,
  title: 30,
  description: 40,
  project: 22,
  duration: 12,
  hourlyRate: 14,
  totalAmount: 16,
  billingStatus: 18,
};

const HEADER_BG = 'FF2563EB';
const HEADER_FG = 'FFFFFFFF';
const ZEBRA_BG = 'FFF9FAFB';
const TOTAL_BG = 'FFDBEAFE';
const BOX_BG_LIGHT = 'FFEFF6FF';
const BOX_BG_DARK = 'FFDCE7FB';
const BOX_BORDER = 'FF93C5FD';
const BORDER_COLOR = 'FFD1D5DB';
const TITLE_COLOR = 'FF1E3A8A';
const STAT_LABEL_COLOR = 'FF6B7280';
const STAT_VALUE_COLOR = 'FF111827';
const MUTED_COLOR = 'FF9CA3AF';

const BILLED_BADGE_BG = 'FFD1FAE5';
const BILLED_BADGE_FG = 'FF065F46';
const UNBILLED_BADGE_BG = 'FFFEF3C7';
const UNBILLED_BADGE_FG = 'FF92400E';

const DATE_LOCALES = { 'en-US': enUS, 'pt-BR': ptBR } as const;
const DATE_FORMATS = { 'en-US': 'yyyy-MM-dd', 'pt-BR': 'dd/MM/yyyy' } as const;

const STAT_LABELS = {
  projects: { en: 'Projects', pt: 'Projetos' },
  hours: { en: 'Hours', pt: 'Horas' },
  billedValue: { en: 'Value (Billed only)', pt: 'Value (Billed only)' },
  unbilledHours: { en: 'Unbilled Hours', pt: 'Unbilled Hours' },
  titleLabel: { en: 'Totals', pt: 'Totais' },
  projectsSuffix: { en: 'projects', pt: 'projects' },
  ofWord: { en: 'of', pt: 'of' },
  totalLabel: { en: 'Total (Billed only)', pt: 'Total (Billed only)' },
} as const;

type StatLabelKey = keyof typeof STAT_LABELS;

function formatDateByLocale(date: Date, locale: ExportLocale): string {
  return format(date, DATE_FORMATS[locale], { locale: DATE_LOCALES[locale] });
}

function formatDurationHuman(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours}h ${mins}m`;
}

function formatCurrencyLabel(currency: string): string {
  return currency === 'USD' ? '$' : currency === 'BRL' ? 'R$' : currency;
}

function getStatLabel(key: StatLabelKey, locale: ExportLocale): string {
  return STAT_LABELS[key][locale === 'pt-BR' ? 'pt' : 'en'];
}

function getColumnLabel(column: ColumnKey, locale: ExportLocale): string {
  return COLUMN_LABELS[column][locale === 'pt-BR' ? 'pt' : 'en'];
}

function formatCurrencyValue(value: number, currency: string, locale: ExportLocale): string {
  const formatted = value.toLocaleString(locale === 'pt-BR' ? 'pt-BR' : 'en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `${formatCurrencyLabel(currency)} ${formatted}`;
}

function encodeColumn(idx: number): string {
  let n = idx;
  let result = '';
  while (n >= 0) {
    result = String.fromCharCode(65 + (n % 26)) + result;
    n = Math.floor(n / 26) - 1;
  }
  return result;
}

function buildFilename(filters: ReportFilters, locale: ExportLocale): string {
  const sanitize = (s: string) => s.replace(/[^0-9-]/g, '');
  if (filters.startDate || filters.endDate) {
    const start = filters.startDate ? sanitize(filters.startDate) : 'start';
    const end = filters.endDate ? sanitize(filters.endDate) : 'end';
    return `Day Sheetly — Time report — ${start} to ${end}.xlsx`;
  }
  const today = format(new Date(), 'yyyy-MM-dd');
  const dateLabel = locale === 'pt-BR' ? 'Relatório de horas' : 'Time report';
  return `Day Sheetly — ${dateLabel} — ${today}.xlsx`;
}

interface BuildRowOptions {
  entry: ReportTask;
  columns: ColumnKey[];
  locale: ExportLocale;
}

function buildDataRow({ entry, columns, locale }: BuildRowOptions): Record<string, string | number> {
  const row: Record<string, string | number> = {};
  for (const column of columns) {
    const label = getColumnLabel(column, locale);
    switch (column) {
      case 'date':
        row[label] = formatDateByLocale(parseLocalDate(entry.date), locale);
        break;
      case 'member':
        row[label] = entry.members.map((m) => m.name).join(', ');
        break;
      case 'title':
        row[label] = entry.title;
        break;
      case 'description':
        row[label] = stripHtml(entry.description);
        break;
      case 'project':
        if (entry.project) {
          row[label] = entry.project.color
            ? `● ${entry.project.name}`
            : entry.project.name;
        } else {
          // Project-less entries (issue #10) use the brand primary blue
          // for the ● dot so the cell is visually consistent.
          row[label] = `● ${'No project'}`;
        }
        break;
      case 'duration':
        row[label] = formatDurationHuman(entry.durationMinutes);
        break;
      case 'hourlyRate':
        row[label] = entry.hourlyRate;
        break;
      case 'totalAmount':
        row[label] = entry.billingStatus === 'billed' ? entry.totalAmount : '----';
        break;
      case 'billingStatus':
        row[label] = capitalize(entry.billingStatus);
        break;
    }
  }
  return row;
}

interface BuildTotalRowOptions {
  entries: ReportTask[];
  columns: ColumnKey[];
  locale: ExportLocale;
}

function buildTotalRow({ entries, columns, locale }: BuildTotalRowOptions): Record<string, string | number> {
  const totalMinutes = entries.reduce((sum, e) => sum + e.durationMinutes, 0);
  const totalAmount = entries
    .filter((e) => e.billingStatus === 'billed')
    .reduce((sum, e) => sum + e.totalAmount, 0);
  const row: Record<string, string | number> = {};
  const totalLabel = getStatLabel('totalLabel', locale);
  for (const column of columns) {
    const label = getColumnLabel(column, locale);
    switch (column) {
      case 'date':
        row[label] = totalLabel;
        break;
      case 'duration':
        row[label] = formatDurationHuman(totalMinutes);
        break;
      case 'totalAmount':
        row[label] = totalAmount;
        break;
      default:
        row[label] = '';
    }
  }
  return row;
}

export interface ExcelProjectSummary {
  id: string;
  name: string;
  color?: string | null;
}

export interface ExcelStats {
  projectCount: number;
  hours: number;
  billedValue: number;
  unbilledHours: number;
  currency: string;
  currencyLabel: string;
}

export interface ExportOptions {
  locale?: ExportLocale;
  /**
   * Project IDs to include in the header box stats.
   * - `undefined` → all available projects selected (default "todos marcados")
   * - `[]`       → none selected (stats = 0)
   * - `[id1, …]` → only these IDs are counted in stats
   */
  selectedProjectIds?: string[];
  /**
   * List of selectable projects. Defaults to the unique projects
   * derived from `entries`.
   */
  availableProjects?: ExcelProjectSummary[];
}

function computeStats(
  entries: ReportTask[],
  selectedIds: Set<string>,
  currency: string,
): ExcelStats {
  // Project-less entries (issue #10) are excluded from project-scoped
  // stats — they have no id to match against selectedIds, and there is no
  // project row in the header box for them.
  const filtered = entries.filter((e) => e.project && selectedIds.has(e.project.id));
  return {
    projectCount: new Set(filtered.map((e) => e.project!.id)).size,
    hours: filtered.reduce((sum, e) => sum + e.durationMinutes, 0),
    billedValue: filtered
      .filter((e) => e.billingStatus === 'billed')
      .reduce((sum, e) => sum + e.totalAmount, 0),
    unbilledHours: filtered
      .filter((e) => e.billingStatus !== 'billed')
      .reduce((sum, e) => sum + e.durationMinutes, 0),
    currency,
    currencyLabel: formatCurrencyLabel(currency),
  };
}

function buildHeaderRow(columns: ColumnKey[], locale: ExportLocale): Record<string, string> {
  const row: Record<string, string> = {};
  for (const column of columns) {
    const label = getColumnLabel(column, locale);
    row[label] = label;
  }
  return row;
}

function extractAvailableProjects(entries: ReportTask[]): ExcelProjectSummary[] {
  const map = new Map<string, ExcelProjectSummary>();
  for (const entry of entries) {
    // Project-less entries (issue #10) bucket under a synthetic id so the
    // header box still sums them. Users picking projects in the export
    // popover wouldn't unselect these without an explicit "No project"
    // row, which we don't surface today.
    if (!entry.project) continue;
    if (!map.has(entry.project.id)) {
      map.set(entry.project.id, {
        id: entry.project.id,
        name: entry.project.name,
        color: entry.project.color,
      });
    }
  }
  return Array.from(map.values());
}

export async function exportToExcel(
  entries: ReportTask[],
  columns: ColumnKey[],
  filters: ReportFilters,
  options: ExportOptions = {},
): Promise<void> {
  if (entries.length === 0) return;

  const locale: ExportLocale = options.locale ?? 'en-US';
  const isPt = locale === 'pt-BR';
  const projectCurrency = entries[0]?.currency ?? 'BRL';

  const availableProjects = options.availableProjects ?? extractAvailableProjects(entries);
  const selectedIds =
    options.selectedProjectIds === undefined
      ? new Set(availableProjects.map((p) => p.id))
      : new Set(options.selectedProjectIds);

  const stats = computeStats(entries, selectedIds, projectCurrency);
  const emptySeparator = {};
  const headerRow = buildHeaderRow(columns, locale);
  const dataRows = entries.map((entry) => buildDataRow({ entry, columns, locale }));
  const totalRow = buildTotalRow({ entries, columns, locale });

  const allRows: Record<string, string | number>[] = [
    {}, // placeholder for title row (set below via getCell + richText)
    {}, // placeholder for stats row (set below via getCell + richText)
    emptySeparator, // row 3: visual gap that continues the header box gradient
    headerRow, // row 4: column header (Date | Project | ...)
    ...dataRows,
    totalRow,
  ];

  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet(isPt ? 'Relatório' : 'Report');

  worksheet.columns = columns.map((column) => ({
    key: getColumnLabel(column, locale),
    width: COLUMN_WIDTHS[column],
  }));

  allRows.forEach((row) => {
    worksheet.addRow(row);
  });

  const numColumns = columns.length;
  const lastCol = encodeColumn(numColumns - 1);
  const fullRange = (rowIdx: number) => `A${rowIdx}:${lastCol}${rowIdx}`;

  // Row indices (1-based)
  const TITLE_ROW = 1;
  const STATS_ROW = 2;
  const GAP_ROW = 3;
  const HEADER_ROW_IDX = 4;
  const DATA_START = 5;

  const selectedCount = availableProjects.filter((p) => selectedIds.has(p.id)).length;
  const totalCount = availableProjects.length;

  // ─── Row 1: Title "TOTALS (3 OF 3 PROJECTS)" ────────────────────────
  if (numColumns > 1) {
    worksheet.mergeCells(fullRange(TITLE_ROW));
  }
  const titleText = `📊  ${getStatLabel('titleLabel', locale)} (${selectedCount} ${getStatLabel('ofWord', locale)} ${totalCount} ${getStatLabel('projectsSuffix', locale)})`;
  const titleCell = worksheet.getRow(TITLE_ROW).getCell(1);
  titleCell.value = {
    richText: [
      { font: { bold: true, size: 12, color: { argb: TITLE_COLOR } }, text: titleText },
    ],
  };
  titleCell.alignment = { vertical: 'middle', horizontal: 'left' };
  titleCell.fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: BOX_BG_DARK },
  };
  for (let c = 1; c <= numColumns; c++) {
    const cell = worksheet.getRow(TITLE_ROW).getCell(c);
    cell.border = {
      top: { style: 'medium', color: { argb: BOX_BORDER } },
      bottom: { style: 'thin', color: { argb: BOX_BORDER } },
    };
  }
  worksheet.getRow(TITLE_ROW).height = 22;

  // ─── Row 2: 4 stats side-by-side ───────────────────────────────────
  // Layout for 7 default columns:
  //   A2       | B2        | C2:E2 merged        | F2:G2 merged
  //   Projects | Hours     | Value (Billed only) | Unbilled Hours
  const statCells = planStatCellLayout(numColumns);
  const statValues = [
    { label: getStatLabel('projects', locale), value: String(stats.projectCount) },
    { label: getStatLabel('hours', locale), value: formatDurationHuman(stats.hours) },
    {
      label: getStatLabel('billedValue', locale),
      value: formatCurrencyValue(stats.billedValue, stats.currency, locale),
      subtitle: stats.currency,
    },
    { label: getStatLabel('unbilledHours', locale), value: formatDurationHuman(stats.unbilledHours) },
  ];

  for (let i = 0; i < statCells.length; i++) {
    const { startCol, endCol } = statCells[i];
    const startLetter = encodeColumn(startCol - 1);
    const endLetter = encodeColumn(endCol - 1);
    const rangeStr = `${startLetter}${STATS_ROW}:${endLetter}${STATS_ROW}`;
    if (startCol !== endCol) {
      worksheet.mergeCells(rangeStr);
    }
    const cell = worksheet.getRow(STATS_ROW).getCell(startCol);
    const stat = statValues[i];
    const richText: ExcelJS.RichText[] = [
      {
        font: {
          bold: true,
          size: 9,
          color: { argb: STAT_LABEL_COLOR },
        },
        text: stat.label.toUpperCase(),
      },
      { font: { size: 9 }, text: '\n' },
      {
        font: {
          bold: true,
          size: 16,
          color: { argb: STAT_VALUE_COLOR },
        },
        text: stat.value,
      },
    ];
    if (stat.subtitle) {
      richText.push(
        { font: { size: 9 }, text: '\n' },
        {
          font: {
            bold: false,
            italic: true,
            size: 8,
            color: { argb: STAT_LABEL_COLOR },
          },
          text: stat.subtitle,
        },
      );
    }
    cell.value = { richText };
    cell.alignment = {
      vertical: 'middle',
      horizontal: 'left',
      wrapText: true,
    };
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: BOX_BG_LIGHT },
    };
    // Vertical divider on the right (skip the last stat)
    if (i < statCells.length - 1) {
      cell.border = {
        right: { style: 'thin', color: { argb: BOX_BORDER } },
        bottom: { style: 'thin', color: { argb: BOX_BORDER } },
      };
    } else {
      cell.border = {
        bottom: { style: 'thin', color: { argb: BOX_BORDER } },
      };
    }
  }
  worksheet.getRow(STATS_ROW).height = 48;

  // ─── Row 3: visual gap that continues the header box gradient ──────
  for (let c = 1; c <= numColumns; c++) {
    const cell = worksheet.getRow(GAP_ROW).getCell(c);
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: BOX_BG_LIGHT },
    };
  }
  worksheet.getRow(GAP_ROW).height = 14;

  // ─── Apply black border around the entire header box (rows 1-3) ─────
  // Top edge: top border on row 1 cells
  // Bottom edge: bottom border on row 3 cells
  // Left edge: left border on column 1 cells of rows 1-3
  // Right edge: right border on column N cells of rows 1-3
  const boxBorder: ExcelJS.Border = {
    style: 'thin',
    color: { argb: 'FF000000' },
  };
  for (let c = 1; c <= numColumns; c++) {
    // Top of box (row 1)
    const topCell = worksheet.getRow(TITLE_ROW).getCell(c);
    topCell.border = {
      ...(topCell.border ?? {}),
      top: boxBorder,
    };
    // Bottom of box (row 3)
    const bottomCell = worksheet.getRow(GAP_ROW).getCell(c);
    bottomCell.border = {
      ...(bottomCell.border ?? {}),
      bottom: boxBorder,
    };
  }
  for (let r = TITLE_ROW; r <= GAP_ROW; r++) {
    // Left edge of box (column 1)
    const leftCell = worksheet.getRow(r).getCell(1);
    leftCell.border = {
      ...(leftCell.border ?? {}),
      left: boxBorder,
    };
    // Right edge of box (column N)
    const rightCell = worksheet.getRow(r).getCell(numColumns);
    rightCell.border = {
      ...(rightCell.border ?? {}),
      right: boxBorder,
    };
  }

  // ─── Row 4: HEADER row ──────────────────────────────────────────────
  const headerRowWs = worksheet.getRow(HEADER_ROW_IDX);
  headerRowWs.eachCell({ includeEmpty: false }, (cell) => {
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: HEADER_BG },
    };
    cell.font = { bold: true, color: { argb: HEADER_FG }, size: 11 };
    cell.alignment = { vertical: 'middle', horizontal: 'left' };
    cell.border = {
      top: { style: 'medium', color: { argb: HEADER_BG } },
      bottom: { style: 'medium', color: { argb: HEADER_BG } },
      left: { style: 'thin', color: { argb: BORDER_COLOR } },
      right: { style: 'thin', color: { argb: BORDER_COLOR } },
    };
  });
  headerRowWs.height = 24;

  // Freeze panes below the header row
  worksheet.views = [{ state: 'frozen', xSplit: 0, ySplit: HEADER_ROW_IDX }];

  // ─── Data rows + zebra ──────────────────────────────────────────────
  for (let i = 0; i < dataRows.length; i++) {
    const rowIndex = DATA_START + i;
    const row = worksheet.getRow(rowIndex);
    const entry = entries[i];
    row.eachCell({ includeEmpty: false }, (cell) => {
      cell.border = {
        top: { style: 'thin', color: { argb: BORDER_COLOR } },
        bottom: { style: 'thin', color: { argb: BORDER_COLOR } },
        left: { style: 'thin', color: { argb: BORDER_COLOR } },
        right: { style: 'thin', color: { argb: BORDER_COLOR } },
      };
      cell.alignment = { vertical: 'middle' };
    });
    if (i % 2 === 1) {
      row.eachCell({ includeEmpty: false }, (cell) => {
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: ZEBRA_BG },
        };
      });
    }
    // Project column: color the "●" with project.color (default brand blue
    // for project-less entries — issue #10).
    const projColIdx = columns.indexOf('project');
    if (projColIdx >= 0) {
      const projectColor = entry.project?.color ?? NO_PROJECT_COLOR;
      const projCell = row.getCell(projColIdx + 1);
      projCell.font = { color: { argb: projectColorToArgb(projectColor) } };
    }
    // Value column: format currency + muted for unbilled
    const valueColIdx = columns.indexOf('totalAmount');
    if (valueColIdx >= 0) {
      const valueCell = row.getCell(valueColIdx + 1);
      const value = valueCell.value;
      if (typeof value === 'number') {
        valueCell.numFmt = projectCurrency === 'USD' ? '"$"#,##0.00' : '"R$"#,##0.00';
        valueCell.alignment = { vertical: 'middle', horizontal: 'right' };
      } else {
        valueCell.alignment = { vertical: 'middle', horizontal: 'right' };
        valueCell.font = { color: { argb: MUTED_COLOR } };
      }
    }
    const rateColIdx = columns.indexOf('hourlyRate');
    if (rateColIdx >= 0) {
      const rateCell = row.getCell(rateColIdx + 1);
      if (typeof rateCell.value === 'number') {
        rateCell.numFmt = projectCurrency === 'USD' ? '"$"#,##0.00' : '"R$"#,##0.00';
        rateCell.alignment = { vertical: 'middle', horizontal: 'right' };
      }
    }
    const durColIdx = columns.indexOf('duration');
    if (durColIdx >= 0) {
      const durCell = row.getCell(durColIdx + 1);
      durCell.alignment = { vertical: 'middle', horizontal: 'right' };
    }
    // Status column: badge with colored fill
    const statusColIdx = columns.indexOf('billingStatus');
    if (statusColIdx >= 0) {
      const statusCell = row.getCell(statusColIdx + 1);
      const isBilled = entry.billingStatus === 'billed';
      statusCell.value = {
        richText: [
          {
            font: {
              bold: true,
              size: 10,
              color: {
                argb: isBilled ? BILLED_BADGE_FG : UNBILLED_BADGE_FG,
              },
            },
            text: isBilled ? ' Billed ' : ' Unbilled ',
          },
        ],
      };
      statusCell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: {
          argb: isBilled ? BILLED_BADGE_BG : UNBILLED_BADGE_BG,
        },
      };
      statusCell.alignment = { vertical: 'middle', horizontal: 'center' };
    }
  }

  // ─── Total row ──────────────────────────────────────────────────────
  const totalRowWs = worksheet.getRow(allRows.length);
  totalRowWs.eachCell({ includeEmpty: false }, (cell) => {
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: TOTAL_BG },
    };
    cell.font = { bold: true, color: { argb: 'FF1E3A8A' }, size: 11 };
    cell.border = {
      top: { style: 'medium', color: { argb: HEADER_BG } },
      bottom: { style: 'thin', color: { argb: BORDER_COLOR } },
      left: { style: 'thin', color: { argb: BORDER_COLOR } },
      right: { style: 'thin', color: { argb: BORDER_COLOR } },
    };
  });
  const totalValueColIdx = columns.indexOf('totalAmount');
  if (totalValueColIdx >= 0) {
    const cell = totalRowWs.getCell(totalValueColIdx + 1);
    if (typeof cell.value === 'number') {
      cell.numFmt = projectCurrency === 'USD' ? '"$"#,##0.00' : '"R$"#,##0.00';
      cell.alignment = { vertical: 'middle', horizontal: 'right' };
    }
  }
  const totalDurColIdx = columns.indexOf('duration');
  if (totalDurColIdx >= 0) {
    totalRowWs.getCell(totalDurColIdx + 1).alignment = {
      vertical: 'middle',
      horizontal: 'right',
    };
  }

  workbook.worksheets[0].state = 'visible';

  // NOTE: writeBuffer + manual download (writeFile uses fs.createWriteStream
  // which doesn't exist in the browser — pre-existing bug from PR #18).
  const filename = buildFilename(filters, locale);
  return workbook.xlsx
    .writeBuffer()
    .then((buffer: ArrayBuffer) => {
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    });
}

/**
 * Distribute 4 stats across numColumns Excel columns, returning inclusive ranges.
 * - For ≥5 columns (the normal case): 1 + 1 + 2 + (rest)
 * - For <5 columns: each stat gets 1 column (compact); later stats clamp to last col
 */
function planStatCellLayout(numColumns: number): Array<{ startCol: number; endCol: number }> {
  const lastCol = Math.max(1, numColumns);
  if (numColumns < 5) {
    // Compact: 1 col each, clamp to last available
    return [
      { startCol: 1, endCol: 1 },
      { startCol: 2, endCol: Math.min(2, lastCol) },
      { startCol: Math.min(3, lastCol), endCol: Math.min(3, lastCol) },
      { startCol: Math.min(4, lastCol), endCol: lastCol },
    ];
  }
  // Standard: 1 + 1 + 2 + (rest)
  return [
    { startCol: 1, endCol: 1 },
    { startCol: 2, endCol: 2 },
    { startCol: 3, endCol: 4 },
    { startCol: 5, endCol: lastCol },
  ];
}

/**
 * Convert a CSS hex color (#RRGGBB or #RGB) to ARGB hex for ExcelJS.
 * ExcelJS expects ARGB (Alpha + RGB). Returns black if input is invalid.
 */
function projectColorToArgb(color: string | null | undefined): string {
  if (!color) return 'FF000000';
  const hex = color.startsWith('#') ? color.slice(1) : color;
  let normalized = hex.toUpperCase();
  if (normalized.length === 3) {
    normalized = normalized
      .split('')
      .map((c) => c + c)
      .join('');
  }
  if (normalized.length !== 6) return 'FF000000';
  return `FF${normalized}`;
}